import React from 'react';
import { Language } from '../../pages/DevPortal';

export default function AuthSection({ lang }: { lang: Language }) {
    const isEn = lang === 'en';

    return (
        <div className="animate-fade-in">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {isEn ? 'Authentication & Authorization' : 'Autenticación y Autorización'}
            </h1>
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                {isEn 
                    ? 'Hospital Status uses a combination of Zero Trust architecture, PIN-based access, and HMAC-SHA256 signed sessions depending on the entry point.'
                    : 'Hospital Status utiliza una combinación de arquitectura Zero Trust, acceso por PIN, y sesiones firmadas por HMAC-SHA256.'}
            </p>

            <h2 className="text-xl font-bold text-slate-900 mb-4">
                {isEn ? 'Authentication Flows' : 'Flujos de Autenticación'}
            </h2>

            <div className="space-y-6 mb-12">
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center gap-3">
                        <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded">PUBLIC</span>
                        <h3 className="font-bold text-slate-900">1. Family View</h3>
                    </div>
                    <div className="p-5">
                        <p className="text-sm text-slate-600">
                            {isEn ? 'Strictly read-only access. Families authenticate implicitly by possessing the exact patient admission code (e.g., MT001). No session is created.' : 'Acceso estrictamente de solo lectura. Las familias acceden implícitamente poseyendo el código exacto de admisión del paciente.'}
                        </p>
                    </div>
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center gap-3">
                        <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-1 rounded">STAFF</span>
                        <h3 className="font-bold text-slate-900">2. Staff Operative Access</h3>
                    </div>
                    <div className="p-5">
                        <p className="text-sm text-slate-600 mb-3">
                            {isEn ? 'A double-factor workflow designed for usability in fast-paced OR environments:' : 'Flujo de doble factor diseñado para velocidad en quirófanos:'}
                        </p>
                        <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
                            <li>{isEn ? 'Identify hospital via dynamic link or global routing.' : 'Identificación del hospital por link o ruta.'}</li>
                            <li>{isEn ? 'Enter 4-digit individual PIN.' : 'Ingresar PIN individual de 4 dígitos.'}</li>
                            <li>{isEn ? 'Backend verifies hashed PIN and issues signed cookie.' : 'Backend valida el PIN cifrado y emite cookie firmada.'}</li>
                        </ol>
                    </div>
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center gap-3">
                        <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded">ENTERPRISE</span>
                        <h3 className="font-bold text-slate-900">3. Admin Zero Trust Access</h3>
                    </div>
                    <div className="p-5">
                        <p className="text-sm text-slate-600">
                            {isEn ? 'Super Admin and Group Admin access is gated through Cloudflare Access. Requires verified email/SSO before the API even receives the request.' : 'Accesos administrativos están protegidos por Cloudflare Access (SSO de la organización).'}
                        </p>
                    </div>
                </div>
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-4">
                {isEn ? 'RBAC (Role-Based Access Control)' : 'Control de Acceso (RBAC)'}
            </h2>
            <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
                <table className="w-full text-left border-collapse text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 font-semibold text-slate-900">Role</th>
                            <th className="px-4 py-3 font-semibold text-slate-900">Scope</th>
                            <th className="px-4 py-3 font-semibold text-slate-900">Capabilities</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-600">
                        <tr>
                            <td className="px-4 py-3 font-mono text-xs">SUPER_ADMIN</td>
                            <td className="px-4 py-3">Global</td>
                            <td className="px-4 py-3">Create hospitals, manage platform.</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-3 font-mono text-xs">GROUP_ADMIN</td>
                            <td className="px-4 py-3">Multi-Hospital</td>
                            <td className="px-4 py-3">Analytics oversight across chain.</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-3 font-mono text-xs">HOSPITAL_ADMIN</td>
                            <td className="px-4 py-3">Single Hospital</td>
                            <td className="px-4 py-3">Manage staff, ads, access audit logs.</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-3 font-mono text-xs">FINANCIERO</td>
                            <td className="px-4 py-3">Single Hospital</td>
                            <td className="px-4 py-3">Read-only analytics and reports.</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-3 font-mono text-xs">OPERATIVO</td>
                            <td className="px-4 py-3">Single Hospital</td>
                            <td className="px-4 py-3">Update patient surgical status.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
